/**
 * QuizJustifiedMasterPanel — agrupa respostas por opção escolhida.
 *
 * Mostra cada pergunta do quiz com contagem por opção e lista de
 * justificativas anonimizadas agrupadas. Mestre pode ler em tempo real
 * o raciocínio dos alunos.
 */

import type { MissionComponentProps } from '../../../games/atlas/components'
import { useLabComponentState } from './useLabComponentState'

interface Response {
  membershipId?: string
  questionId?: string
  optionId?: string
  isCorrect?: boolean
  justification?: string
}

interface QuestionConfig {
  id: string
  prompt: string
  options: Array<{ id: string; text: string }>
  correctOptionId: string
}

export function QuizJustifiedMasterPanel(props: MissionComponentProps) {
  const slideIndex = props.slideIndex ?? 0
  const snap = useLabComponentState('lab.assess.quizJustified', slideIndex)
  const config = props.slideConfig ?? {}
  const questions = ((config.questions as QuestionConfig[]) || []).filter(
    (q) => q && q.id,
  )
  const responses = (snap?.aggregate.responses as Response[]) ?? []
  const total = snap?.totalPlayers ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Respostas por pergunta</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {total} aluno(s) na sessão
        </p>
      </header>

      {questions.map((q) => {
        const forQ = responses.filter((r) => r.questionId === q.id)
        const byOption: Record<string, Response[]> = {}
        for (const r of forQ) {
          if (!r.optionId) continue
          byOption[r.optionId] = byOption[r.optionId] ?? []
          byOption[r.optionId].push(r)
        }
        return (
          <div
            key={q.id}
            style={{
              padding: 12,
              background: 'var(--color-lab-bg-2, #F2F2ED)',
              borderRadius: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ fontSize: '0.9em', fontWeight: 500 }}>{q.prompt}</div>
            {q.options.map((o) => {
              const list = byOption[o.id] ?? []
              const isCorrect = o.id === q.correctOptionId
              return (
                <div key={o.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.85em',
                      color: isCorrect ? '#0a7837' : undefined,
                      fontWeight: isCorrect ? 600 : 400,
                    }}
                  >
                    <span>{isCorrect ? '✓ ' : ''}{o.text}</span>
                    <span>{list.length}</span>
                  </div>
                  {list.length > 0 && (
                    <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {list.map((r, i) => (
                        <li
                          key={`${r.membershipId}-${i}`}
                          style={{
                            fontSize: '0.8em',
                            opacity: 0.85,
                            paddingLeft: 8,
                            borderLeft: '2px solid var(--color-lab-rule, #D8D5CB)',
                          }}
                        >
                          {r.justification}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
